import type { HelmInquiryStatus, WorkflowOperationalState } from './api/types'
import type { HelmConsignmentStatus } from './database.types'

/**
 * What a status means to the person scanning a list.
 *
 * The design system has one red accent and does everything else in neutrals,
 * so a status colour has to say something rather than decorate. Each status
 * is one of these:
 *
 * - `waiting` (amber): someone else has to act - a customer, a forwarder, a payment.
 * - `moving` (blue): work is under way.
 * - `done` (green): the record has reached its end.
 * - `blocked` (red): stuck. The only red status, so it stands out.
 * - `neutral`: not started.
 * - `closed`: finished without needing attention; recedes.
 */
export const STATUS_TONES = ['neutral', 'waiting', 'moving', 'done', 'blocked', 'closed'] as const
export type StatusTone = (typeof STATUS_TONES)[number]

// Typed as complete records so a status added to the API cannot silently fall
// through to `neutral`.
const INQUIRY: Record<HelmInquiryStatus, StatusTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'waiting',
  QUALIFYING: 'moving',
  QUOTED: 'waiting',
  ACTIVE: 'moving',
  BLOCKED: 'blocked',
  COMPLETED: 'done',
  CANCELLED: 'closed',
}

const CONSIGNMENT: Record<HelmConsignmentStatus, StatusTone> = {
  DRAFT: 'neutral',
  AWAITING_INVOICE_FROM_FORWARDER: 'waiting',
  AWAITING_PAYMENT: 'waiting',
  // Paid, not yet shipped: progress, not an ending.
  PAYMENT_CONFIRMED: 'moving',
  IN_TRANSIT_DOMESTIC: 'moving',
  IN_TRANSIT_INTERNATIONAL: 'moving',
  ARRIVED: 'moving',
  CLEARING: 'moving',
  // The goods wait on the customer to collect them.
  READY_FOR_PICKUP: 'waiting',
  DELIVERED: 'done',
  CLOSED: 'closed',
}

const OPERATIONAL: Record<WorkflowOperationalState, StatusTone> = {
  ACTIVE: 'moving',
  BLOCKED: 'blocked',
  COMPLETED: 'done',
  CANCELLED: 'closed',
}

// Statuses from before the Helm workflow, still present on old consignments.
const LEGACY: Record<string, StatusTone> = {
  pending: 'waiting',
  processing: 'moving',
  completed: 'done',
  cancelled: 'closed',
}

const TONES: Record<string, StatusTone> = { ...LEGACY, ...OPERATIONAL, ...CONSIGNMENT, ...INQUIRY }

/** Labels short enough for a table cell; anything else is title-cased. */
const LABELS: Record<string, string> = {
  AWAITING_INVOICE_FROM_FORWARDER: 'Awaiting Invoice',
  IN_TRANSIT_DOMESTIC: 'Domestic Transit',
  IN_TRANSIT_INTERNATIONAL: 'International Transit',
}

export function statusTone(status: string | null | undefined): StatusTone {
  return (status && TONES[status]) || 'neutral'
}

export function statusLabel(status: string | null | undefined): string {
  if (!status) return 'Not set'
  return (
    LABELS[status] ??
    status
      .toLowerCase()
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  )
}
